import { useState, useEffect, useCallback } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Send, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_STYLES, formatDateShort } from './hrUtils';

export const RequestsAdminTab = () => {
  const [requests, setRequests] = useState(null);
  const [replyText, setReplyText] = useState({});

  const load = useCallback(() => {
    hrAPI.listRequests().then((res) => setRequests(res.data)).catch(() => setRequests([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const reply = async (id) => {
    const text = (replyText[id] || '').trim();
    if (!text) return;
    try {
      await hrAPI.replyRequest(id, text);
      setReplyText({ ...replyText, [id]: '' });
      toast.success('Reply sent');
      load();
    } catch (err) {
      toast.error('Failed to reply');
    }
  };

  const close = async (id) => {
    try {
      await hrAPI.setRequestStatus(id, 'closed');
      toast.success('Request closed');
      load();
    } catch (err) {
      toast.error('Failed to close');
    }
  };

  if (!requests) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  const open = requests.filter((r) => r.status !== 'closed');
  const closed = requests.filter((r) => r.status === 'closed');

  const RequestCard = ({ r, showActions }) => (
    <div className="p-4 rounded-xl border border-slate-200 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-medium text-slate-800">{r.subject}</p>
        <Badge variant="secondary" className="capitalize">{r.category}</Badge>
        <Badge className={STATUS_STYLES[r.status] || ''}>{r.status}</Badge>
        <span className="text-xs text-slate-400 ml-auto">{r.employee_name} · {formatDateShort(r.created_at)}</span>
      </div>
      <p className="text-sm text-slate-600 whitespace-pre-wrap">{r.message}</p>
      {(r.responses || []).map((resp, i) => (
        <div key={i} className={`p-3 rounded-lg text-sm ${resp.is_admin ? 'bg-cyan-50 border border-cyan-100' : 'bg-slate-50 border border-slate-100'}`}>
          <p className="text-xs font-medium text-slate-500 mb-1">{resp.by_name}{resp.is_admin ? ' (HR)' : ''} · {formatDateShort(resp.created_at)}</p>
          <p className="text-slate-700 whitespace-pre-wrap">{resp.message}</p>
        </div>
      ))}
      {showActions && (
        <div className="flex gap-2">
          <Input
            placeholder="Reply to employee..."
            value={replyText[r.id] || ''}
            onChange={(e) => setReplyText({ ...replyText, [r.id]: e.target.value })}
            className="bg-white border-slate-200"
            data-testid={`hr-admin-reply-input-${r.id}`}
          />
          <Button size="icon" className="bg-cyan-500 hover:bg-cyan-600 rounded-full shrink-0" onClick={() => reply(r.id)} data-testid={`hr-admin-reply-btn-${r.id}`}>
            <Send className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="outline" className="rounded-full shrink-0" title="Close request" onClick={() => close(r.id)} data-testid={`hr-admin-close-btn-${r.id}`}>
            <Archive className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="hr-admin-requests-tab">
      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-slate-800 text-lg">Open HR Requests <Badge className="bg-blue-100 text-blue-700 ml-2">{open.length}</Badge></CardTitle></CardHeader>
        <CardContent className="space-y-3" data-testid="hr-admin-open-requests">
          {open.length === 0 && <p className="text-sm text-slate-400 py-2">No open requests.</p>}
          {open.map((r) => <RequestCard key={r.id} r={r} showActions />)}
        </CardContent>
      </Card>

      {closed.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-slate-800 text-lg">Closed <Badge variant="secondary" className="ml-2">{closed.length}</Badge></CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {closed.slice(0, 10).map((r) => <RequestCard key={r.id} r={r} showActions={false} />)}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
